import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Camera, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { TALENT_TYPES, CREATOR_TYPES } from "@/lib/roleCategories";

export default function Onboarding() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState("talent");
  const [form, setForm] = useState({
    bio: "",
    location: "",
    experience: "",
    talent_type: "",
    company_name: "",
    pro_type: "",
    description: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (user?.role) {
      if (user.role === "talent") navigate("/talent");
      else if ((user.role === "creator" || user.role === "professional")) navigate("/createurs");
      else navigate("/admin");
    }
  }, [user, loading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { role, ...form };
      await api.post("/auth/role", payload);
      await checkAuth();
      toast.success("Bienvenue sur Helencia !");
      if (role === "talent") navigate("/talent");
      else navigate("/createurs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création du profil");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="onboarding-page">
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-16">
        <p className="caption-label text-[#C68E59] mb-4">Bienvenue {user.name}</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mb-3">
          Créons votre profil
        </h1>
        <p className="text-[#666] mb-10">Quelques informations pour démarrer.</p>

        <form onSubmit={handleSubmit} className="space-y-8">
          <div className="card-lux p-8">
            <Label className="caption-label mb-4 block">Je suis</Label>
            <RadioGroup
              value={role}
              onValueChange={setRole}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <label
                className={`card-lux p-6 cursor-pointer flex items-start gap-4 ${role === "talent" ? "border-[#1A1A1A] ring-1 ring-[#1A1A1A]" : ""}`}
                data-testid="role-talent-card"
              >
                <RadioGroupItem value="talent" id="role-talent" className="mt-1" />
                <div>
                  <Camera className="w-6 h-6 text-[#C68E59] mb-2" />
                  <p className="font-serif text-xl text-[#1A1A1A]">Je suis un Talent</p>
                  <p className="text-sm text-[#666] mt-1">
                    Mannequin, modèle, acteur, danseur, performer, influenceur — vous incarnez l'image.
                  </p>
                </div>
              </label>

              <label
                className={`card-lux p-6 cursor-pointer flex items-start gap-4 ${role === "creator" || role === "professional" ? "border-[#1A1A1A] ring-1 ring-[#1A1A1A]" : ""}`}
                data-testid="role-pro-card"
              >
                <RadioGroupItem value="creator" id="role-pro" className="mt-1" />
                <div>
                  <Sparkles className="w-6 h-6 text-[#C68E59] mb-2" />
                  <p className="font-serif text-xl text-[#1A1A1A]">Je suis un Créateur</p>
                  <p className="text-sm text-[#666] mt-1">
                    Photographe, styliste, MUA, vidéaste, marque, studio, agence — vous créez et recherchez des talents.
                  </p>
                </div>
              </label>
            </RadioGroup>
          </div>

          {role === "talent" ? (
            <div className="card-lux p-8 space-y-6">
              <h3 className="font-serif text-2xl text-[#1A1A1A]">Votre profil talent</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="talent_type" className="caption-label">Type de talent</Label>
                  <Select value={form.talent_type} onValueChange={(v) => setForm({ ...form, talent_type: v })}>
                    <SelectTrigger className="rounded-xl mt-2" data-testid="talent-type-select">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {TALENT_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="location" className="caption-label">Localisation</Label>
                  <Input
                    id="location"
                    placeholder="Paris, France"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="rounded-xl mt-2"
                    data-testid="location-input"
                  />
                </div>
                <div>
                  <Label htmlFor="experience" className="caption-label">Expérience</Label>
                  <Input
                    id="experience"
                    placeholder="2 ans, débutant..."
                    value={form.experience}
                    onChange={(e) => setForm({ ...form, experience: e.target.value })}
                    className="rounded-xl mt-2"
                    data-testid="experience-input"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="bio" className="caption-label">Biographie</Label>
                <Textarea
                  id="bio"
                  placeholder="Parlez de votre parcours…"
                  value={form.bio}
                  onChange={(e) => setForm({ ...form, bio: e.target.value })}
                  rows={4}
                  className="rounded-xl mt-2"
                  data-testid="bio-input"
                />
              </div>
            </div>
          ) : (
            <div className="card-lux p-8 space-y-6">
              <h3 className="font-serif text-2xl text-[#1A1A1A]">Votre profil créateur</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="company_name" className="caption-label">Nom (studio, marque, ou votre nom)</Label>
                  <Input
                    id="company_name"
                    placeholder="Maison Célesta · Studio Reyel"
                    value={form.company_name}
                    onChange={(e) => setForm({ ...form, company_name: e.target.value })}
                    className="rounded-xl mt-2"
                    data-testid="company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="pro_type" className="caption-label">Métier</Label>
                  <Select value={form.pro_type} onValueChange={(v) => setForm({ ...form, pro_type: v })}>
                    <SelectTrigger className="rounded-xl mt-2" data-testid="pro-type-select">
                      <SelectValue placeholder="Sélectionner" />
                    </SelectTrigger>
                    <SelectContent>
                      {CREATOR_TYPES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="pro_location" className="caption-label">Localisation</Label>
                  <Input
                    id="pro_location"
                    placeholder="Paris, France"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    className="rounded-xl mt-2"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description" className="caption-label">Description</Label>
                <Textarea
                  id="description"
                  placeholder="Présentez votre activité…"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  className="rounded-xl mt-2"
                  data-testid="description-input"
                />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-10 py-6"
              data-testid="onboarding-submit-btn"
            >
              {submitting ? "Création…" : "Créer mon profil"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
